
import React, { useCallback, useEffect, useState } from "react";
import CommonLayout from "../CommonLayout";
import {
  Button,
  Form,
  Input,
  message,
  Typography,
  Spin,
} from "antd";
import { useForm } from "antd/es/form/Form";
import { useTranslation } from "react-i18next";
import {
  SafetyCertificateOutlined,
  LockOutlined,
  MobileOutlined,
  DeleteOutlined,
  PlusOutlined,
  ArrowRightOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import "./index.css";

import {
  getMobileIdentifiers,
  deleteIdentifier,
  verifyAndDeleteIdentifier,
  addIdentifier,
  addIdentifierVerification,
} from "../../../Services/IdentifierService/Identifier";

import { translation } from "../../../i18n/translationConstant";
import OTPInputComponent from "../../Common/Components/OTPModal";
import { AddIdentifier } from "../../../Models/Request/Identifier/AddIdentifier";
import { IdentifierCategory } from "../../../Utils/Constants/identifierType";
import { useFingerprintValidation } from "../../Authentication/FingerprintValidationContext";

interface MobileIdentifier {
  id: string;
  type: string;
  value: string;
  categoryType: string;
  primary: boolean;
}

interface MobileNumberFormValues {
  secondaryNumber?: string;
}

const MobileNumbers: React.FC = () => {
  const [form] = useForm<MobileNumberFormValues>();
  const { t } = useTranslation();
  const { checkFingerprint } = useFingerprintValidation();

  const [mobileIdentifiers, setMobileIdentifiers] = useState<
    MobileIdentifier[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const [isAddOTPModalVisible, setIsAddOTPModalVisible] = useState(false);
  const [addOtpUniqueID, setAddOtpUniqueID] = useState("");
  const [selectedAddIdentifier, setSelectedAddIdentifier] =
    useState<AddIdentifier | null>(null);

  const [isDeleteOTPModalVisible, setIsDeleteOTPModalVisible] =
    useState(false);
  const [deleteOtpUniqueID, setDeleteOtpUniqueID] = useState("");
  const [selectedIdentifierId, setSelectedIdentifierId] = useState("");
  const [selectedDeleteMobileNumber, setSelectedDeleteMobileNumber] =
    useState("");
  const [selectedDeleteIdentifier, setSelectedDeleteIdentifier] =
    useState<MobileIdentifier | null>(null);

  const getMobileIdentifiersFiltered = useCallback(() => {
    setLoading(true);

    getMobileIdentifiers()
      .then((res) => {
        setMobileIdentifiers(res.data || []);
      })
      .catch((error) => {
        console.error("Get mobile identifiers error:", error);
        message.error(t("lblUnableToLoadMobileNumbers"));
      })
      .finally(() => {
        setLoading(false);
        setInitialLoading(false);
      });
  }, [t]);

  useEffect(() => {
    getMobileIdentifiersFiltered();
    checkFingerprint();
  }, [getMobileIdentifiersFiltered, checkFingerprint]);

  const primaryNumber = mobileIdentifiers.find(
    (identifier) => identifier.primary
  );

  const secondaryNumbers = mobileIdentifiers.filter(
    (identifier) => !identifier.primary
  );

  const canAddMobileNumber = mobileIdentifiers.length <= 1;

  const formSubmit = (values: MobileNumberFormValues) => {
    const mobileNumber = values.secondaryNumber?.trim();

    if (!mobileNumber) {
      message.error(t("errorMobileNumber"));
      return;
    }

    if (!/^[0-9]{10}$/.test(mobileNumber)) {
      message.error(t("errorInvalidPhoneNumber"));
      return;
    }

    const identifier: AddIdentifier = {
      type: "MOBILE",
      categoryType: IdentifierCategory.STRONG,
      value: mobileNumber,
    };

    setLoading(true);

    addIdentifier(identifier)
      .then((res) => {
        setSelectedAddIdentifier(identifier);
        setAddOtpUniqueID(res.data.mobile.otpUniqueID);
        setIsAddOTPModalVisible(true);
      })
      .catch((error) => {
        console.error("Add identifier error:", error);
        // message.error(
        //   error?.response?.data?.message ||
        //     t("lblUnableToAddMobileNumber")
        // );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const verifyAddMobileNumber = (otp: string) => {
    if (!selectedAddIdentifier) {
      return;
    }

    addIdentifierVerification({
      code: otp,
      otpUniqueID: addOtpUniqueID,
      identifierValue: selectedAddIdentifier.value,
      categoryType: selectedAddIdentifier.categoryType,
      type: selectedAddIdentifier.type,
    })
      .then(() => {
        message.success(t("lblMobileNumberAddedSuccessfully"));

        setIsAddOTPModalVisible(false);
        setAddOtpUniqueID("");
        setSelectedAddIdentifier(null);
        form.resetFields(["secondaryNumber"]);

        getMobileIdentifiersFiltered();
      })
      .catch((error) => {
        console.error("Verify add identifier error:", error);
        message.error(
          error?.response?.data?.message ||
            t("errorEnterValidOTP")
        );
      });
  };

  const resendAddOTP = () => {
    if (!selectedAddIdentifier) {
      return;
    }

    addIdentifier(selectedAddIdentifier)
      .then((res) => {
        setAddOtpUniqueID(res.data.mobile.otpUniqueID);
        message.success(t("lblOTPSendSuccess"));
      })
      .catch((error) => {
        console.error("Resend add OTP error:", error);
        message.error(
          error?.response?.data?.message ||
            t("lblUnableToResendOtp")
        );
      });
  };

  const closeAddOTPModal = () => {
    setIsAddOTPModalVisible(false);
    setAddOtpUniqueID("");
    setSelectedAddIdentifier(null);
  };

  const deleteMobileNumber = (identifier: MobileIdentifier) => {
    setSelectedDeleteIdentifier(identifier);
    setSelectedIdentifierId(identifier.id);
    setSelectedDeleteMobileNumber(identifier.value);

    deleteIdentifier(identifier.id)
      .then((res) => {
        setDeleteOtpUniqueID(res.data.mobile.otpUniqueID);
        setIsDeleteOTPModalVisible(true);
      })
      .catch((error) => {
        console.error("Delete identifier error:", error);
        // message.error(
        //   error?.response?.data?.message ||
        //     t("lblUnableToDeleteMobileNumber")
        // );

        setSelectedDeleteIdentifier(null);
        setSelectedIdentifierId("");
        setSelectedDeleteMobileNumber("");
      });
  };

  const verifyDeleteMobileNumber = (otp: string) => {
    if (!selectedIdentifierId) {
      return;
    }

    verifyAndDeleteIdentifier({
      id: selectedIdentifierId,
      otpUniqueID: deleteOtpUniqueID,
      code: otp,
    })
      .then(() => {
        message.success(t("lblMobileNumberDeletedSuccessfully"));

        setIsDeleteOTPModalVisible(false);
        setSelectedDeleteIdentifier(null);
        setSelectedIdentifierId("");
        setSelectedDeleteMobileNumber("");
        setDeleteOtpUniqueID("");

        getMobileIdentifiersFiltered();
      })
      .catch((error) => {
        console.error("Verify delete identifier error:", error);
        message.error(
          error?.response?.data?.message ||
            t("errorEnterValidOTP")
        );
      });
  };

  const resendDeleteOTP = () => {
    if (!selectedDeleteIdentifier) {
      return;
    }

    deleteIdentifier(selectedDeleteIdentifier.id)
      .then((res) => {
        setDeleteOtpUniqueID(res.data.mobile.otpUniqueID);
        message.success(t("lblOTPSendSuccess"));
      })
      .catch((error) => {
        console.error("Resend delete OTP error:", error);
        message.error(
          error?.response?.data?.message ||
            t("lblUnableToResendOtp")
        );
      });
  };

  const closeDeleteOTPModal = () => {
    setIsDeleteOTPModalVisible(false);
    setSelectedDeleteIdentifier(null);
    setSelectedIdentifierId("");
    setSelectedDeleteMobileNumber("");
    setDeleteOtpUniqueID("");
  };

  return (
    <>
      <OTPInputComponent
        title={t(translation.lblVerify)}
        isOpen={isAddOTPModalVisible}
        onOtpSubmit={verifyAddMobileNumber}
        onCancel={closeAddOTPModal}
        okText={t(translation.lblConfirm)}
        onResend={resendAddOTP}
        mobileNumber={selectedAddIdentifier?.value || ""}
      />

      <OTPInputComponent
        title={t(translation.lblVerify)}
        isOpen={isDeleteOTPModalVisible}
        onOtpSubmit={verifyDeleteMobileNumber}
        onCancel={closeDeleteOTPModal}
        okText={t(translation.lblConfirm)}
        onResend={resendDeleteOTP}
        mobileNumber={selectedDeleteMobileNumber}
      />

      <CommonLayout
        title={t(translation.lblMobileNumbers)}
        showBackButton={true}
      >
        <div className="mobileRedesignPage">
          {/* Description on left, security badge on right */}
          <header className="mobileRedesignHeader">
            <div className="mobileRedesignHeaderText">
              <Typography.Text className="mobileRedesignPageSubtitle">
                {t("lblMobileNumbersDescription")}
              </Typography.Text>
            </div>

            <span className="mobileRedesignEyebrow">
              <SafetyCertificateOutlined />
              {t(translation.lblYourInformationIsSecure)}
            </span>
          </header>

          {/* Centered illustration */}
          <div
            className="mobileRedesignHero"
            aria-hidden="true"
          >
            <div className="mobileRedesignHeroGlow" />

            <div className="mobileRedesignHeroIcon">
              <MobileOutlined />

              <span className="mobileRedesignHeroCheck">
                <CheckCircleFilled />
              </span>
            </div>

            <span className="mobileRedesignHeroDot mobileRedesignHeroDotOne" />
            <span className="mobileRedesignHeroDot mobileRedesignHeroDotTwo" />
            <span className="mobileRedesignHeroDot mobileRedesignHeroDotThree" />
          </div>

          <div className="mobileRedesignContent">
            <section className="mobileRedesignCard">
              <div className="mobileRedesignCardHeading">
                <div>
                  <Typography.Title
                    level={4}
                    className="mobileRedesignSectionTitle"
                  >
                    {t("lblRegisteredMobileNumbers")}
                  </Typography.Title>

                  <Typography.Text className="mobileRedesignSectionSubtitle">
                    {t("lblMobileNumbersCardDescription")}
                  </Typography.Text>
                </div>

                <div className="mobileRedesignCount">
                  <MobileOutlined />
                  <span>{mobileIdentifiers.length}</span>
                </div>
              </div>

              <div className="mobileRedesignDivider" />

              {initialLoading ? (
                <div className="mobileRedesignLoading">
                  <Spin size="large" />
                  <span>{t("lblLoadingMobileNumbers")}</span>
                </div>
              ) : (
                <>
                  <div className="mobileRedesignNumberSection">
                    <div className="mobileRedesignFieldHeading">
                      <span className="mobileRedesignFieldIcon mobileRedesignFieldIconPrimary">
                        <LockOutlined />
                      </span>

                      <div className="mobileRedesignFieldText">
                        <Typography.Text className="mobileRedesignFieldTitle">
                          {t("lblPrimaryMobileNumbers")}
                        </Typography.Text>

                        <Typography.Text className="mobileRedesignFieldHint">
                          {t("lblPrimaryMobileNumbersDesc")}
                        </Typography.Text>
                      </div>

                      {primaryNumber && (
                        <span className="mobileRedesignPrimaryTag">
                          <CheckCircleFilled />
                          {t("lblCurrentlyInUse")}
                        </span>
                      )}
                    </div>

                    {primaryNumber ? (
                      <div className="mobileRedesignPrimaryNumber">
                        <span className="mobileRedesignCountryCode">
                          +91
                        </span>

                        <span className="mobileRedesignNumberValue">
                          {primaryNumber.value}
                        </span>

                        <LockOutlined className="mobileRedesignTrailingLock" />
                      </div>
                    ) : (
                      <div className="mobileRedesignEmptyPrimary">
                        {t("lblNoPrimaryMobileNumber")}
                      </div>
                    )}
                  </div>

                  <div className="mobileRedesignNumberSection mobileRedesignSecondarySection">
                    <div className="mobileRedesignFieldHeading">
                      <span className="mobileRedesignFieldIcon mobileRedesignFieldIconSecondary">
                        <MobileOutlined />
                      </span>

                      <div className="mobileRedesignFieldText">
                        <Typography.Text className="mobileRedesignFieldTitle">
                          {t("lblSecondaryMobileNumbers")}
                        </Typography.Text>

                        <Typography.Text className="mobileRedesignFieldHint">
                          {t("lblSecondaryMobileNumbersDesc")}
                        </Typography.Text>
                      </div>

                      <span className="mobileRedesignOptionalTag">
                        {t("lblOptional")}
                      </span>
                    </div>

                    {secondaryNumbers.length > 0 ? (
                      <div className="mobileRedesignSecondaryList">
                        {secondaryNumbers.map((identifier) => (
                          <div
                            className="mobileRedesignSecondaryRow"
                            key={identifier.id}
                          >
                            <div className="mobileRedesignSecondaryNumber">
                              <span className="mobileRedesignCountryCode">
                                +91
                              </span>

                              <span className="mobileRedesignNumberValue">
                                {identifier.value}
                              </span>
                            </div>

                            <Button
                              type="text"
                              danger
                              className="mobileRedesignDeleteButton"
                              icon={<DeleteOutlined />}
                              onClick={() => deleteMobileNumber(identifier)}
                            >
                              {t("lblDelete")}
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="mobileRedesignNoSecondary">
                        <MobileOutlined />
                        <span>{t("lblNoSecondaryMobileNumber")}</span>
                      </div>
                    )}
                  </div>

                  {canAddMobileNumber && (
                    <div className="mobileRedesignAddSection">
                      <div className="mobileRedesignAddHeading">
                        <div className="mobileRedesignAddIcon">
                          <PlusOutlined />
                        </div>

                        <div>
                          <Typography.Title
                            level={5}
                            className="mobileRedesignAddTitle"
                          >
                            {t("lblNewMobileNumber")}
                          </Typography.Title>

                          <Typography.Text className="mobileRedesignAddSubtitle">
                            {t("lblAddSecondaryMobileNumberHint")}
                          </Typography.Text>
                        </div>
                      </div>

                      <Form
                        form={form}
                        name="mobileNumberForm"
                        onFinish={formSubmit}
                        autoComplete="off"
                        layout="vertical"
                        className="mobileRedesignForm"
                        scrollToFirstError
                      >
                        <Form.Item
                          name="secondaryNumber"
                          className="mobileRedesignInputItem"
                          rules={[
                            {
                              required: true,
                              message: t("errorMobileNumber"),
                            },
                            {
                              pattern: /^[0-9]{10}$/,
                              message: t("errorInvalidPhoneNumber"),
                            },
                          ]}
                        >
                          <Input
                            className="mobileRedesignInput"
                            placeholder={t("lblEnter10DigitMobileNumber")}
                            maxLength={10}
                            inputMode="numeric"
                            prefix={
                              <span className="mobileRedesignInputPrefix">
                                <span>+91</span>
                                <span className="mobileRedesignInputDivider" />
                              </span>
                            }
                            onChange={(event) => {
                              const digitsOnly =
                                event.target.value.replace(/\D/g, "");

                              form.setFieldsValue({
                                secondaryNumber: digitsOnly,
                              });
                            }}
                          />
                        </Form.Item>

                        <div className="mobileRedesignFormFooter">
                          <Typography.Text className="mobileRedesignOtpHint">
                            <SafetyCertificateOutlined />
                            {t("lblSecondaryMobileNumbersDesc")}
                          </Typography.Text>

                          <Button
                            htmlType="submit"
                            loading={loading}
                            className="mobileRedesignSubmitButton"
                          >
                            {t("lblUpdateDetails")}
                            <ArrowRightOutlined />
                          </Button>
                        </div>
                      </Form>
                    </div>
                  )}
                </>
              )}
            </section>

            <div className="mobileRedesignSecurityNote">
              <SafetyCertificateOutlined />
              <span>{t("lblMobileNumberSecurityNote")}</span>
            </div>
          </div>
        </div>
      </CommonLayout>
    </>
  );
};

export default MobileNumbers;
